import { PAGE_BREAK_TOKEN } from './constants'

/**
 * Markers are for the reader, not for the person editing.
 *
 * The edit box binds straight to the display text, so a reader who taps "Edytuj"
 * has been seeing `<STRONA>` between pages and, since emphasis shipped, `**` around
 * every bold span. Raw markup in front of a dyslexic reader is the thing this app
 * exists to avoid — and worse, the markers are editable: deleting one silently
 * removes a page separator from the reader and a section break from the RTF export,
 * with nothing to connect the two.
 *
 * So the editor gets clean text, and the markers are put back on save.
 *
 * This is deliberately NOT the fix for P1.6. The token still lives in the text
 * everywhere else; this stops it reaching the one surface where a human can edit
 * it by hand. See docs/task-p1.6-page-breaks.md.
 */
export interface EditableText {
  /** What the textarea shows: no page tokens, no emphasis asterisks. */
  text: string
  /**
   * Where the markers were, described by the text around them rather than by
   * character offsets — an offset is invalidated by the first keystroke, and the
   * whole point is that the text is about to be edited.
   */
  pageBreakAnchors: PageBreakAnchor[]
  emphasisSpans: string[]
}

const EMPHASIS_PATTERN = /\*\*([^*]+)\*\*/g

/** How much surrounding text identifies a page break well enough to find it again. */
const ANCHOR_LENGTH = 60

/** A page break remembered by the text on both sides of it. */
interface PageBreakAnchor {
  /** End of the page before it. */
  tail: string
  /** Start of the page after it. */
  head: string
}

export function toEditableText(text: string): EditableText {
  const source = String(text ?? '')

  /*
   * Emphasis comes off first, and the order matters.
   *
   * The anchors are searched for in the *edited* text, which no longer has any
   * asterisks in it. Capturing them from the marked-up source produced anchors
   * containing `**`, so they matched nothing and every page break after the first
   * was silently dropped.
   */
  const emphasisSpans: string[] = []
  const clean = source.replace(EMPHASIS_PATTERN, (_match, inner: string) => {
    emphasisSpans.push(inner)
    return inner
  })

  /*
   * Anchors are cut from the finished editable text, not from the source.
   *
   * They have to be substrings of the string that will actually be searched. Taken
   * from the source they carried whitespace the join and the blank-line collapse
   * then changed - a tail of "\nRozdział." could not be found in text where that
   * newline had moved - and every break after the first was silently dropped.
   */
  const parts = clean.split(PAGE_BREAK_TOKEN).map((part) => part.trim())
  const editable = parts.join('\n\n')

  const pageBreakAnchors: PageBreakAnchor[] = []
  let cursor = 0
  let previousBreakEnd = 0
  for (let i = 1; i < parts.length; i++) {
    cursor += parts[i - 1].length
    /*
     * Neither anchor may reach past a neighbouring break.
     *
     * With two short pages the 60-character tail for the second break ran back over
     * the first one. In the source that is a contiguous string; in the restored text
     * a `<STRONA>` now sits in the middle of it, so the match landed at the end of
     * the document and the break was dropped by the empty-page guard.
     */
    const nextBreakAt = cursor + 2 + parts[i].length
    pageBreakAnchors.push({
      tail: editable.slice(Math.max(previousBreakEnd, cursor - ANCHOR_LENGTH), cursor),
      head: editable.slice(cursor + 2, Math.min(nextBreakAt, cursor + 2 + ANCHOR_LENGTH)),
    })
    cursor += 2 // the '\n\n' this break became
    previousBreakEnd = cursor
  }

  return { text: editable, pageBreakAnchors, emphasisSpans }
}

/**
 * Put the markers back around whatever the person left behind.
 *
 * Each marker is re-applied only where it can still be located, and the search
 * moves forward so a repeated phrase cannot pull a later marker backwards. A
 * marker that cannot be placed is **dropped, not guessed** — a page separator in
 * the wrong place is worse than one that is missing, and inventing emphasis the
 * page never had is worse still.
 */
export function fromEditableText(edited: string, original: EditableText): string {
  let result = String(edited ?? '')

  let breakSearchFrom = 0
  for (const anchor of original.pageBreakAnchors) {
    /*
     * Both sides are tried, because either can be the one that was edited. The
     * tail comes first: a page break belongs after the previous page ended, and
     * anchoring on what follows loses the break whenever someone corrects the
     * first sentence of a page.
     */
    let insertAt = -1
    if (anchor.tail) {
      const at = result.indexOf(anchor.tail, breakSearchFrom)
      if (at !== -1) insertAt = at + anchor.tail.length
    }
    if (insertAt === -1 && anchor.head) {
      const at = result.indexOf(anchor.head, breakSearchFrom)
      if (at !== -1) insertAt = at
    }
    if (insertAt === -1) continue

    const before = result.slice(0, insertAt).replace(/\s+$/, '')
    const after = result.slice(insertAt).replace(/^\s+/, '')
    /*
     * A page break needs a page after it. Anchoring on the tail keeps a break
     * findable when the page that followed was edited - including when it was
     * deleted outright, which would otherwise leave the document ending on a
     * separator with nothing behind it.
     */
    if (after.length === 0) continue
    result = `${before}\n${PAGE_BREAK_TOKEN}\n${after}`
    // Measured from the rebuilt string: trimming either side shifts everything
    // after it, so an offset derived from the pre-insertion position lands short.
    breakSearchFrom = before.length + 2 + PAGE_BREAK_TOKEN.length
  }

  /*
   * Emphasis last, and the order is load-bearing.
   *
   * The page anchors were measured against text with no asterisks in it. Restoring
   * emphasis first put `**` back into the very spans the anchors were cut from, so
   * a tail like "...i to jest ważne dla czytelnika." no longer matched and the
   * break before the next page was silently dropped. Spans never cross a page
   * break, so running this pass afterwards costs nothing.
   */
  let searchFrom = 0
  for (const span of original.emphasisSpans) {
    if (!span) continue
    const at = result.indexOf(span, searchFrom)
    if (at === -1) continue
    result = `${result.slice(0, at)}**${span}**${result.slice(at + span.length)}`
    searchFrom = at + span.length + 4
  }

  return result
}
